/**
 * Authentication Service
 *
 * Manages session authentication and credential lifecycle.
 */

import { ApiClient } from '@api/client';
import { SESSION_CONFIG } from '@utils/constants';
import type { AuthState, AuthCredentials } from '@/types/auth.types';
import { logger } from '@utils/logger';

/**
 * Authentication Service Class
 *
 * Handles:
 * - Initial authentication (POST /api/sessions/{sessionId}/auth)
 * - Credential storage in memory (NOT localStorage)
 * - Session expiry detection
 */
export class AuthenticationService {
  private apiClient: ApiClient;
  private credentials: AuthCredentials | null = null;
  private expiresAt: string | null = null;
  private expiryCheckInterval: number | null = null;

  /**
   * Create authentication service
   *
   * @param apiClient - API client instance
   */
  constructor(apiClient: ApiClient) {
    this.apiClient = apiClient;
  }

  /**
   * Set credentials from window.__PAYMENT_SESSION__
   *
   * Called after BFF embeds credentials in HTML (Option B).
   * Pure setter — no API call, stores in memory only.
   *
   * @param credentials - Credentials object from window.__PAYMENT_SESSION__
   */
  setCredentials(credentials: {
    sessionId: string;
    apiKey: string;
    paymentId: string;
    agentSessionId: string;
    expiresAt: string;
  }): void {
    logger.info('Setting credentials from payment session', {
      hasApiKey: !!credentials.apiKey,
      hasSessionId: !!credentials.sessionId,
      expiresAt: credentials.expiresAt,
    });

    this.credentials = {
      apiKey: credentials.apiKey,
      sessionId: credentials.sessionId,
      paymentId: credentials.paymentId,
      agentSessionId: credentials.agentSessionId,
    };

    this.expiresAt = credentials.expiresAt;

    this.apiClient.setCredentials(credentials.apiKey, credentials.sessionId);

    this.startExpiryMonitoring();
  }

  /**
   * Check if authenticated
   *
   * @returns true if credentials are set, false otherwise
   */
  isAuthenticated(): boolean {
    return this.credentials !== null && this.apiClient.isAuthenticated();
  }

  /**
   * Get current authentication state
   *
   * @returns Auth state object
   */
  getAuthState(): AuthState {
    return {
      isAuthenticated: this.isAuthenticated(),
      isLoading: false,
      error: null,
      expiresAt: this.expiresAt || undefined,
      timeRemaining: this.getTimeRemaining() ?? undefined,
    };
  }

  /**
   * Clear authentication state
   *
   * Called on logout or session expiry
   */
  clearCredentials(): void {
    this.credentials = null;
    this.expiresAt = null;
    this.apiClient.clearCredentials();
    this.stopExpiryMonitoring();

    logger.info('Authentication cleared');
  }

  /**
   * Get API credentials (for testing/debugging)
   *
   * @returns Current credentials or null
   */
  getCredentials(): AuthCredentials | null {
    return this.credentials;
  }

  /**
   * Start monitoring session expiry
   *
   * Checks expiry every minute and logs warnings
   */
  private startExpiryMonitoring(): void {
    // Clear existing interval
    this.stopExpiryMonitoring();

    // Check expiry every minute
    this.expiryCheckInterval = window.setInterval(() => {
      this.checkExpiry();
    }, 60000); // 60 seconds

    // Initial check
    this.checkExpiry();
  }

  /**
   * Stop monitoring session expiry
   */
  private stopExpiryMonitoring(): void {
    if (this.expiryCheckInterval !== null) {
      clearInterval(this.expiryCheckInterval);
      this.expiryCheckInterval = null;
    }
  }

  /**
   * Check session expiry and log warnings
   *
   * Logs warning at 5 minutes remaining (28 minutes elapsed)
   * Session expires at 30 minutes
   */
  private checkExpiry(): void {
    if (!this.expiresAt) {
      return;
    }

    const timeRemaining = this.getTimeRemaining();

    if (timeRemaining === null) {
      return;
    }

    // Session expired
    if (timeRemaining <= 0) {
      logger.warn('Session expired');
      this.clearCredentials();
      return;
    }

    // Warning threshold: 5 minutes remaining
    if (timeRemaining <= SESSION_CONFIG.EXPIRY_WARNING_MINUTES * 60 * 1000) {
      const minutesRemaining = Math.ceil(timeRemaining / 60000);
      logger.warn(`Session expires in ${minutesRemaining} minutes`);
    }
  }

  /**
   * Get time remaining until session expires
   *
   * @returns Time remaining in milliseconds, or null if no expiry set
   */
  private getTimeRemaining(): number | null {
    if (!this.expiresAt) {
      return null;
    }

    const expiryTime = new Date(this.expiresAt).getTime();
    const now = Date.now();
    const remaining = expiryTime - now;

    return remaining;
  }

  /**
   * Check if session is about to expire
   *
   * @returns true if session expires in less than 5 minutes
   */
  isExpiringSoon(): boolean {
    const timeRemaining = this.getTimeRemaining();

    if (timeRemaining === null) {
      return false;
    }

    return (
      timeRemaining <= SESSION_CONFIG.EXPIRY_WARNING_MINUTES * 60 * 1000 &&
      timeRemaining > 0
    );
  }

  /**
   * Check if session has expired
   *
   * @returns true if session has expired
   */
  isExpired(): boolean {
    const timeRemaining = this.getTimeRemaining();

    if (timeRemaining === null) {
      return false;
    }

    return timeRemaining <= 0;
  }
}
